import React, { useState, useMemo } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from 'components/AppIcon';

const LME_METALS = [
  { symbol: 'LME_CU', label: 'Cobre',    color: '#EA580C' },
  { symbol: 'LME_AL', label: 'Aluminio', color: '#6366f1' },
  { symbol: 'LME_ZN', label: 'Zinc',     color: '#06b6d4' },
  { symbol: 'LME_NI', label: 'Níquel',   color: '#8b5cf6' },
  { symbol: 'LME_PB', label: 'Plomo',    color: '#64748b' },
];

const TIMEFRAMES = [
  { label: '1d',  ms: 1 * 24 * 60 * 60 * 1000 },
  { label: '7d',  ms: 7 * 24 * 60 * 60 * 1000 },
  { label: '30d', ms: 30 * 24 * 60 * 60 * 1000 },
];

function buildChartData(ticks, symbol, eurUsdTicks, timeframeMs) {
  const cutoff = new Date(Date.now() - timeframeMs);
  const metalTicks = ticks
    ?.filter(t => t?.symbol === symbol && new Date(t.ts) >= cutoff)
    ?.sort((a, b) => new Date(a.ts) - new Date(b.ts));

  return metalTicks?.map(t => {
    const ts = new Date(t.ts);
    const usdVal = parseFloat(t?.value);
    // Find nearest EUR/USD tick
    let eurUsd = null;
    if (eurUsdTicks?.length > 0) {
      const nearest = eurUsdTicks?.reduce((prev, curr) =>
        Math.abs(new Date(curr.ts) - ts) < Math.abs(new Date(prev.ts) - ts) ? curr : prev
      );
      eurUsd = parseFloat(nearest?.value);
    }
    return {
      ts: ts?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
      usd: usdVal,
      eurUsdRate: eurUsd,
    };
  });
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '8px 12px', fontSize: 11 }}>
      <p className="text-muted-foreground mb-1" style={{ fontFamily: 'JetBrains Mono' }}>{label}</p>
      {payload?.map((entry, i) => (
        <p key={i} style={{ color: entry?.color, fontFamily: 'JetBrains Mono' }}>
          {entry?.name === 'usd' ? 'Metal USD/t' : 'EUR/USD'}:{' '}
          <strong>
            {entry?.name === 'usd' ? entry?.value?.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
              : entry?.value?.toLocaleString('es-ES', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
          </strong>
        </p>
      ))}
    </div>
  );
};

export default function BenchmarkTrendCharts({ ticks }) {
  const [timeframe, setTimeframe] = useState('7d');
  const [activeMetal, setActiveMetal] = useState('LME_CU');

  const eurUsdTicks = useMemo(() =>
    ticks?.filter(t => t?.symbol === 'EUR_USD') || []
  , [ticks]);

  const timeframeMs = TIMEFRAMES?.find(t => t?.label === timeframe)?.ms || TIMEFRAMES?.[1]?.ms;

  const chartData = useMemo(() =>
    buildChartData(ticks || [], activeMetal, eurUsdTicks, timeframeMs)
  , [ticks, activeMetal, eurUsdTicks, timeframeMs]);

  const metal = LME_METALS?.find(m => m?.symbol === activeMetal);
  const hasEurUsd = chartData?.some(d => d?.eurUsdRate != null);

  // Compute EUR/USD domain with padding
  const eurUsdValues = chartData?.filter(d => d?.eurUsdRate != null)?.map(d => d?.eurUsdRate);
  const eurUsdMin = eurUsdValues?.length ? Math.min(...eurUsdValues) : 1.0;
  const eurUsdMax = eurUsdValues?.length ? Math.max(...eurUsdValues) : 1.2;
  const eurUsdPad = Math.max((eurUsdMax - eurUsdMin) * 0.3, 0.02);

  return (
    <div className="bg-card border border-border rounded-md p-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Icon name="BarChart2" size={16} color="var(--color-accent)" />
          <h3 className="text-sm font-semibold text-foreground font-caption uppercase tracking-wider">Tendencia de Benchmarks</h3>
        </div>

        {/* Metal selector */}
        <div className="flex gap-1 flex-wrap">
          {LME_METALS?.map(m => (
            <button
              key={m?.symbol}
              onClick={() => setActiveMetal(m?.symbol)}
              className={`px-2.5 py-1 rounded text-xs font-caption transition-all ${
                activeMetal === m?.symbol ? 'text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
              style={activeMetal === m?.symbol ? { background: m?.color } : {}}
            >
              {m?.label}
            </button>
          ))}
        </div>

        {/* EUR/USD badge */}
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-caption bg-muted text-muted-foreground ml-auto">
          <Icon name="Euro" size={12} color="#10b981" />
          <span style={{ color: '#10b981' }}>EUR/USD</span>
          <span className="text-muted-foreground">eje derecho</span>
        </div>

        {/* Timeframe */}
        <div className="flex rounded overflow-hidden border border-border">
          {TIMEFRAMES?.map(tf => (
            <button
              key={tf?.label}
              onClick={() => setTimeframe(tf?.label)}
              className={`px-2.5 py-1 text-xs font-caption transition-all ${
                timeframe === tf?.label
                  ? 'bg-accent text-white' : 'bg-background text-muted-foreground hover:bg-muted'
              }`}
            >
              {tf?.label}
            </button>
          ))}
        </div>
      </div>

      {chartData?.length < 2 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Icon name="BarChart2" size={32} color="var(--color-muted-foreground)" />
          <div className="mt-2 text-sm">Sin suficientes datos para {metal?.label} en {timeframe}</div>
          <div className="text-xs mt-1">Añade ticks manualmente para ver la tendencia</div>
        </div>
      ) : (
        <div style={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 5, right: 55, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="ts"
                tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: 'JetBrains Mono' }}
                tickLine={false}
                interval="preserveStartEnd"
              />
              {/* Left Y-axis: metal price in USD/t */}
              <YAxis
                yAxisId="metal"
                orientation="left"
                tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: 'JetBrains Mono' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => v?.toLocaleString('es-ES', { maximumFractionDigits: 0 })}
                label={{ value: 'USD/t', angle: -90, position: 'insideLeft', offset: -2, style: { fontSize: 9, fill: 'var(--color-muted-foreground)', fontFamily: 'JetBrains Mono' } }}
              />
              {/* Right Y-axis: EUR/USD rate */}
              {hasEurUsd && (
                <YAxis
                  yAxisId="eurusd"
                  orientation="right"
                  domain={[eurUsdMin - eurUsdPad, eurUsdMax + eurUsdPad]}
                  tick={{ fontSize: 10, fill: '#10b981', fontFamily: 'JetBrains Mono' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={v => v?.toFixed(4)}
                  label={{ value: 'EUR/USD', angle: 90, position: 'insideRight', offset: 10, style: { fontSize: 9, fill: '#10b981', fontFamily: 'JetBrains Mono' } }}
                />
              )}
              <Tooltip content={<CustomTooltip />} />
              <Legend
                formatter={v => v === 'usd' ? `${metal?.label} (USD/t)` : 'EUR/USD'}
                wrapperStyle={{ fontSize: 11 }}
              />
              {/* Metal price line */}
              <Line
                yAxisId="metal"
                type="monotone"
                dataKey="usd"
                stroke={metal?.color || 'var(--color-accent)'}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              {/* EUR/USD rate line on secondary axis */}
              {hasEurUsd && (
                <Line
                  yAxisId="eurusd"
                  type="monotone"
                  dataKey="eurUsdRate"
                  stroke="#10b981"
                  strokeWidth={1.5}
                  strokeDasharray="5 3"
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
